import { useEffect, useMemo, useRef, useState } from "react";
import { GripVertical } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { cn } from "@/lib/utils";
import type {
  BuilderMode,
  ProposalDocumentLayout,
  ProposalDocumentLayoutPage,
  TemplatePageElementType,
  TemplatePageLayoutMode,
  TemplatePageSectionType,
  TemplatePageType,
} from "../../types";
import type { LineItemUpdate } from "./BuilderElementPreview";
import { CanvasLeftSidebar } from "./CanvasLeftSidebar";
import { CanvasPageStage, type CanvasSelection } from "./CanvasPageStage";
import { CanvasRightInspector, type SectionDraft } from "./CanvasRightInspector";
import type { BuilderElementDraft } from "./BuilderSectionElementList";

// ---------------------------------------------------------------------------
// ID generators
// ---------------------------------------------------------------------------

function uid(prefix: string) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getPageId() { return uid("page"); }
function getSectionId() { return uid("section"); }
function getElementId() { return uid("element"); }

// ---------------------------------------------------------------------------
// Default content factories
// ---------------------------------------------------------------------------

function defaultTitleForPageType(type: TemplatePageType): string {
  switch (type) {
    case "cover": return "Cover Page";
    case "cover_letter": return "Cover Letter";
    case "line_items": return "Services & Pricing";
    case "static_page": return "Static Page";
    case "custom": return "Custom Page";
    case "closing": return "Closing Page";
    case "signature": return "Signature Page";
    default: return "Page";
  }
}

function defaultSettingsForPageType(
  type: TemplatePageType,
): Record<string, string | boolean | number> {
  switch (type) {
    case "cover": return { showLogo: true, showProspectDetails: true };
    case "cover_letter": return { useBrandCoverLetter: true };
    case "line_items": return { showPricingSummary: true, showTotals: true };
    case "static_page": return { source: "brand_or_template" };
    case "signature": return { requireSignature: true };
    default: return {};
  }
}

function defaultTitleForSectionType(type: TemplatePageSectionType): string {
  switch (type) {
    case "hero": return "Hero Section";
    case "content": return "Content Section";
    case "media": return "Media Section";
    case "services": return "Services Section";
    case "summary": return "Summary Section";
    case "footer": return "Footer Section";
    default: return "Custom Section";
  }
}

function defaultElementsForSectionType(
  type: TemplatePageSectionType,
): BuilderElementDraft[] {
  switch (type) {
    case "hero":
      return [
        {
          id: getElementId(),
          type: "text",
          label: "Headline",
          content: { text: "Add your headline here." },
          style: { fontSize: 30, fontWeight: "bold" },
        },
        {
          id: getElementId(),
          type: "text",
          label: "Supporting Copy",
          content: { text: "Add supporting copy here." },
          style: { fontSize: 16 },
        },
      ];
    case "services":
      return [
        {
          id: getElementId(),
          type: "service_table",
          label: "Service Table",
          content: { source: "proposal_line_items", showHeading: true },
          style: {},
        },
      ];
    case "summary":
      return [
        {
          id: getElementId(),
          type: "project_summary",
          label: "Project Summary",
          content: { source: "proposal_summary", showHeading: true },
          style: {},
        },
      ];
    case "media":
      return [
        {
          id: getElementId(),
          type: "image",
          label: "Image Block",
          content: { src: "", alt: "", caption: "" },
          style: {},
        },
      ];
    case "footer":
      return [
        {
          id: getElementId(),
          type: "text",
          label: "Footer Copy",
          content: { text: "Add footer messaging here." },
          style: { fontSize: 14 },
        },
      ];
    default:
      return [
        {
          id: getElementId(),
          type: "text",
          label: "Text Block",
          content: { text: "Add content here." },
          style: { fontSize: 16 },
        },
      ];
  }
}

function defaultContentForElementType(
  type: TemplatePageElementType,
): Record<string, string | boolean | number | null> {
  switch (type) {
    case "text": return { text: "Add your text here." };
    case "rich_text": return { html: "<p>Add rich text here.</p>" };
    case "image": return { src: "", alt: "" };
    case "divider": return { style: "solid" };
    case "spacer": return { height: 24 };
    case "badge": return { text: "Badge" };
    case "company_info": return { source: "brand_settings", showHeading: true };
    case "project_summary": return { source: "proposal_summary", showHeading: true };
    case "service_table": return { source: "proposal_line_items", showHeading: true };
    case "pricing_summary": return { source: "proposal_totals", showHeading: true };
    case "signature_block": return { source: "signature_request", showHeading: true };
    case "static_content": return { text: "Reusable static content." };
    default: return {};
  }
}

function defaultLabelForElementType(type: TemplatePageElementType): string {
  const labels: Partial<Record<TemplatePageElementType, string>> = {
    text: "Text Block",
    rich_text: "Rich Text Block",
    image: "Image Block",
    logo: "Logo",
    divider: "Divider",
    spacer: "Spacer",
    badge: "Badge",
    company_info: "Company Info",
    project_summary: "Project Summary",
    service_table: "Service Table",
    pricing_summary: "Pricing Summary",
    signature_block: "Signature Block",
    static_content: "Static Content",
    custom: "Custom Element",
  };
  return labels[type] ?? "Element";
}

function defaultSectionsForPageType(type: TemplatePageType) {
  const el = (
    elType: TemplatePageElementType,
    label: string,
    content: Record<string, string | boolean | number | null>,
    style: Record<string, string | boolean | number | null> = {},
  ) => ({ id: getElementId(), type: elType, label, content, style });

  const sec = (
    secType: TemplatePageSectionType,
    title: string,
    order: number,
    elements: ReturnType<typeof el>[],
  ) => ({
    id: getSectionId(),
    title,
    type: secType,
    order,
    layoutMode: "stack" as TemplatePageLayoutMode,
    settings: {},
    elements,
  });

  switch (type) {
    case "cover":
      return [sec("hero", "Hero Section", 0, [
        el("logo",    "Logo",     { src: "", alt: "Company Logo" }, { width: 160 }),
        el("text",    "Headline", { text: "Proposal for [Client Name]" }, { fontSize: 36, fontWeight: "bold" }),
        el("text",    "Tagline",  { text: "Prepared by [Company Name]" }, { fontSize: 16, color: "#6b7280" }),
      ])];

    case "cover_letter":
      return [sec("content", "Cover Letter", 0, [
        el("company_info", "Company Info", { source: "brand_settings", showHeading: false }),
        el("rich_text",    "Letter Body",  { source: "brand_cover_letter", html: "<p>Dear [Client Name],</p><p>Thank you for the opportunity to submit this proposal. We look forward to working with you.</p>" }),
        el("text",         "Sign-off",     { text: "[Signoff],\n[Company Name]" }, { fontSize: 15 }),
      ])];

    case "line_items":
      return [
        sec("services", "Services Section", 0, [
          el("service_table",  "Service Table",  { source: "proposal_line_items", showHeading: true, headingText: "Services & Pricing" }),
        ]),
        sec("summary", "Pricing Summary", 1, [
          el("pricing_summary", "Pricing Summary", { source: "proposal_totals", showHeading: true, headingText: "Pricing Summary" }),
        ]),
      ];

    case "closing":
      return [sec("content", "Closing Section", 0, [
        el("text",         "Closing Heading", { text: "Thank you for your business." }, { fontSize: 28, fontWeight: "bold" }),
        el("text",         "Closing Message", { text: "We look forward to working with you. Please don't hesitate to reach out with any questions." }, { fontSize: 16 }),
        el("company_info", "Company Info",    { source: "brand_settings", showHeading: false }),
      ])];

    case "signature":
      return [sec("custom", "Signature Section", 0, [
        el("signature_block", "Signature Block", { source: "signature_request", showHeading: true, headingText: "Authorization & Signature" }),
      ])];

    case "static_page":
      return [sec("content", "Content Section", 0, [
        el("static_content", "Static Content", { text: "Add your reusable static content here." }, { fontSize: 15 }),
      ])];

    default: // "custom" and anything else
      return [sec("content", "Content Section", 0, [
        el("text", "Text Block", { text: "Add your content here." }, { fontSize: 16 }),
      ])];
  }
}

function createPage(type: TemplatePageType, order: number): ProposalDocumentLayoutPage {
  return {
    id: getPageId(),
    title: defaultTitleForPageType(type),
    type,
    order,
    settings: defaultSettingsForPageType(type),
    sections: defaultSectionsForPageType(type),
  };
}

function createSection(
  type: TemplatePageSectionType,
  order: number,
  layoutMode: TemplatePageLayoutMode = "stack",
) {
  return {
    id: getSectionId(),
    title: defaultTitleForSectionType(type),
    type,
    order,
    layoutMode,
    settings: {},
    elements: defaultElementsForSectionType(type),
  };
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function normalizeLayout(layout?: ProposalDocumentLayout | null): ProposalDocumentLayout {
  const pages = Array.isArray(layout?.pages) ? layout!.pages : [];
  return {
    ...(layout ?? { pages: [] }),
    pages: pages
      .map((page, index) => {
        const pageType = (page.type || "custom") as TemplatePageType;
        const rawSections = Array.isArray(page.sections) ? page.sections : [];
        // Auto-populate structured page types that have no sections yet
        const sections =
          rawSections.length === 0 && pageType !== "custom" && pageType !== "static_page" && pageType !== "closing"
            ? defaultSectionsForPageType(pageType)
            : rawSections;
        return {
          ...page,
          id:       page.id || getPageId(),
          title:    page.title || defaultTitleForPageType(pageType),
          type:     pageType,
          order:    typeof page.order === "number" ? page.order : index,
          settings: page.settings ?? {},
          sections,
        };
      })
      .sort((a, b) => ((a.order as number) ?? 0) - ((b.order as number) ?? 0)),
  };
}

function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function reindexPages(pages: ProposalDocumentLayoutPage[]): ProposalDocumentLayoutPage[] {
  return pages.map((p, i) => ({ ...p, order: i }));
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProposalCanvasContext {
  proposalId?: string;
  prospectName?: string | null;
  prospectAddress?: string | null;
  lineItems?: Array<{
    id: string;
    name: string;
    description?: string | null;
    price?: number | null;
    quantity?: number | null;
    price_unit?: string | null;
  }>;
  subtotal?: number | null;
  total?: number | null;
}

interface ProposalBuilderCanvasProps {
  layout?: ProposalDocumentLayout | null;
  onChange?: (layout: ProposalDocumentLayout) => void;
  builderMode?: BuilderMode;
  readOnly?: boolean;
  isSaving?: boolean;
  className?: string;
  proposalContext?: ProposalCanvasContext;
  inheritedPages?: import("./CanvasLeftSidebar").InheritedStaticPage[];
  onUpdateLineItem?: (id: string, updates: LineItemUpdate) => void;
}

// ---------------------------------------------------------------------------
// DragOverlay ghost
// ---------------------------------------------------------------------------

function DragGhost({ id }: { id: string }) {
  const label = id.startsWith("pg:")
    ? "Page"
    : id.startsWith("sc:")
    ? "Section"
    : "Element";

  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/20 bg-[#1c1e22]/95 px-4 py-2.5 text-sm text-white/80 shadow-2xl backdrop-blur-sm">
      <GripVertical className="h-4 w-4 text-white/40" />
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProposalBuilderCanvas
// ---------------------------------------------------------------------------

export function ProposalBuilderCanvas({
  layout,
  onChange,
  readOnly = false,
  isSaving: _isSaving = false,
  className,
  proposalContext,
  inheritedPages = [],
  onUpdateLineItem,
}: ProposalBuilderCanvasProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [internalLayout, setInternalLayout] = useState<ProposalDocumentLayout>(() =>
    normalizeLayout(layout),
  );
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selection, setSelection] = useState<CanvasSelection>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [inspectorOpen, setInspectorOpen] = useState(true);

  // Tracks when a layout change originated from an internal commit so the
  // prop-sync effect below doesn't overwrite it with a redundant re-normalize.
  const skipSyncRef = useRef(false);

  // Sync external layout changes (e.g. initial load, external save round-trip).
  // Skipped when the change came from commit() to avoid a double state update.
  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    setInternalLayout(normalizeLayout(layout));
  }, [layout]);

  // Auto-select first page
  useEffect(() => {
    const pages = internalLayout.pages;
    if (!selectedPageId && pages.length > 0) {
      setSelectedPageId(pages[0].id ?? null);
      return;
    }
    if (selectedPageId && !pages.some((p) => p.id === selectedPageId)) {
      setSelectedPageId(pages[0]?.id ?? null);
    }
  }, [internalLayout.pages, selectedPageId]);

  // Clear selection when page changes
  useEffect(() => {
    setSelection(null);
  }, [selectedPageId]);

  const selectedPage = useMemo(
    () => internalLayout.pages.find((p) => p.id === selectedPageId) ?? null,
    [internalLayout.pages, selectedPageId],
  );

  // ---------------------------------------------------------------------------
  // Commit helper
  // ---------------------------------------------------------------------------

  function commit(nextLayout: ProposalDocumentLayout) {
    skipSyncRef.current = true;
    setInternalLayout(nextLayout);
    onChange?.(nextLayout);
  }

  function commitPages(nextPages: ProposalDocumentLayoutPage[]) {
    commit({ ...internalLayout, pages: nextPages });
  }

  function updatePageById(
    pageId: string,
    updater: (page: ProposalDocumentLayoutPage) => ProposalDocumentLayoutPage,
  ) {
    commitPages(internalLayout.pages.map((p) => (p.id === pageId ? updater(p) : p)));
  }

  // ---------------------------------------------------------------------------
  // Page operations
  // ---------------------------------------------------------------------------

  function handleAddPage(type: TemplatePageType) {
    if (readOnly) return;
    const page = createPage(type, internalLayout.pages.length);
    const next = reindexPages([...internalLayout.pages, page]);
    commitPages(next);
    setSelectedPageId(page.id ?? null);
  }

  function handleDuplicatePage(pageId: string) {
    if (readOnly) return;
    const page = internalLayout.pages.find((p) => p.id === pageId);
    if (!page) return;
    const idx = internalLayout.pages.findIndex((p) => p.id === pageId);
    const dup: ProposalDocumentLayoutPage = {
      ...page,
      id: getPageId(),
      title: `${page.title ?? "Page"} Copy`,
      settings: { ...(page.settings ?? {}) },
      sections: Array.isArray(page.sections) ? [...page.sections] : [],
    };
    const next = [...internalLayout.pages];
    next.splice(idx + 1, 0, dup);
    commitPages(reindexPages(next));
    setSelectedPageId(dup.id ?? null);
  }

  function handleDeletePage(pageId: string) {
    if (readOnly) return;
    const next = reindexPages(internalLayout.pages.filter((p) => p.id !== pageId));
    commitPages(next);
    if (selectedPageId === pageId) {
      setSelectedPageId(next[0]?.id ?? null);
    }
  }

  function handleChangePageTitle(title: string) {
    if (!selectedPageId) return;
    updatePageById(selectedPageId, (p) => ({ ...p, title }));
  }

  function handleChangePage(updates: Partial<ProposalDocumentLayoutPage>) {
    if (!selectedPageId) return;
    updatePageById(selectedPageId, (p) => ({
      ...p,
      ...updates,
      settings:
        updates.settings !== undefined
          ? { ...(p.settings ?? {}), ...(updates.settings ?? {}) }
          : p.settings,
    }));
  }

  // ---------------------------------------------------------------------------
  // Section operations
  // ---------------------------------------------------------------------------

  function getSections(pageId: string) {
    const page = internalLayout.pages.find((p) => p.id === pageId);
    return Array.isArray(page?.sections)
      ? (page!.sections as Array<Record<string, unknown>>)
      : [];
  }

  function commitSections(
    pageId: string,
    nextSections: Array<Record<string, unknown>>,
  ) {
    updatePageById(pageId, (p) => ({
      ...p,
      sections: nextSections.map((s, i) => ({ ...s, order: i })),
    }));
  }

  function handleAddSection(type: TemplatePageSectionType) {
    if (!selectedPageId || readOnly) return;
    const currentSections = getSections(selectedPageId);
    const newSection = createSection(type, currentSections.length);
    commitSections(selectedPageId, [...currentSections, newSection]);
    setSelection({ kind: "section", sectionId: newSection.id });
  }

  function handleDeleteSection(sectionId: string) {
    if (!selectedPageId || readOnly) return;
    const next = getSections(selectedPageId).filter((s) => s.id !== sectionId);
    commitSections(selectedPageId, next);
    if (selection?.sectionId === sectionId) setSelection(null);
  }

  function handleUpdateSection(sectionId: string, updates: Partial<SectionDraft>) {
    if (!selectedPageId) return;
    const next = getSections(selectedPageId).map((s) =>
      s.id === sectionId ? { ...s, ...updates } : s,
    );
    commitSections(selectedPageId, next);
  }

  // ---------------------------------------------------------------------------
  // Element operations
  // ---------------------------------------------------------------------------

  function getElements(pageId: string, sectionId: string): BuilderElementDraft[] {
    const sections = getSections(pageId);
    const section = sections.find((s) => s.id === sectionId);
    return Array.isArray(section?.elements)
      ? (section!.elements as BuilderElementDraft[])
      : [];
  }

  function commitElements(pageId: string, sectionId: string, nextElements: BuilderElementDraft[]) {
    const nextSections = getSections(pageId).map((s) =>
      s.id === sectionId ? { ...s, elements: nextElements } : s,
    );
    commitSections(pageId, nextSections);
  }

  function handleAddElement(sectionId: string, type: TemplatePageElementType) {
    if (!selectedPageId || readOnly) return;
    const current = getElements(selectedPageId, sectionId);
    const newEl: BuilderElementDraft = {
      id: getElementId(),
      type,
      label: defaultLabelForElementType(type),
      content: defaultContentForElementType(type),
      style: {},
    };
    commitElements(selectedPageId, sectionId, [...current, newEl]);
    setSelection({ kind: "element", sectionId, elementId: newEl.id });
  }

  function handleDeleteElement(sectionId: string, elementId: string) {
    if (!selectedPageId || readOnly) return;
    const next = getElements(selectedPageId, sectionId).filter(
      (e) => e.id !== elementId,
    );
    commitElements(selectedPageId, sectionId, next);
    if (selection?.kind === "element" && selection.elementId === elementId) {
      setSelection({ kind: "section", sectionId });
    }
  }

  function handleUpdateElement(
    sectionId: string,
    elementId: string,
    updates: Partial<BuilderElementDraft>,
  ) {
    if (!selectedPageId) return;
    const next = getElements(selectedPageId, sectionId).map((e) =>
      e.id === elementId
        ? {
            ...e,
            ...updates,
            content:
              updates.content !== undefined
                ? { ...(e.content ?? {}), ...(updates.content ?? {}) }
                : e.content,
            style:
              updates.style !== undefined
                ? { ...(e.style ?? {}), ...(updates.style ?? {}) }
                : e.style,
          }
        : e,
    );
    commitElements(selectedPageId, sectionId, next);
  }

  // ---------------------------------------------------------------------------
  // DnD — drag start / end
  // ---------------------------------------------------------------------------

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeStr = String(active.id);
    const overStr = String(over.id);

    // --- Pages reorder ---
    if (activeStr.startsWith("pg:") && overStr.startsWith("pg:")) {
      const fromId = activeStr.slice(3);
      const toId = overStr.slice(3);
      const from = internalLayout.pages.findIndex((p) => p.id === fromId);
      const to = internalLayout.pages.findIndex((p) => p.id === toId);
      if (from < 0 || to < 0) return;
      commitPages(reindexPages(moveItem(internalLayout.pages, from, to)));
      return;
    }

    if (!selectedPageId) return;
    const sections = getSections(selectedPageId);

    // --- Sections reorder ---
    if (activeStr.startsWith("sc:") && overStr.startsWith("sc:")) {
      const fromId = activeStr.slice(3);
      const toId = overStr.slice(3);
      const from = sections.findIndex((s) => s.id === fromId);
      const to = sections.findIndex((s) => s.id === toId);
      if (from < 0 || to < 0) return;
      commitSections(selectedPageId, moveItem(sections, from, to));
      return;
    }

    // --- Elements reorder within a section ---
    if (activeStr.startsWith("el:") && overStr.startsWith("el:")) {
      const fromElemId = activeStr.slice(3);
      const toElemId = overStr.slice(3);

      // Find which section contains the active element
      const parentSection = sections.find((s) =>
        Array.isArray(s.elements) &&
        (s.elements as BuilderElementDraft[]).some((e) => e.id === fromElemId),
      );
      if (!parentSection) return;

      const elems = getElements(selectedPageId, String(parentSection.id));
      const from = elems.findIndex((e) => e.id === fromElemId);
      const to = elems.findIndex((e) => e.id === toElemId);
      if (from < 0 || to < 0) return;
      commitElements(selectedPageId, String(parentSection.id), moveItem(elems, from, to));
    }
  }

  // ---------------------------------------------------------------------------
  // Inherited pages — create a real editable page from brand static page data
  // ---------------------------------------------------------------------------

  const alreadyAddedStaticPageIds = useMemo(() => {
    return new Set(
      internalLayout.pages
        .map((p) => (p.settings as Record<string, unknown>)?.source_static_page_id)
        .filter(Boolean) as string[],
    );
  }, [internalLayout.pages]);

  const availableInheritedPages = useMemo(
    () => inheritedPages.filter((p) => !alreadyAddedStaticPageIds.has(p.id)),
    [inheritedPages, alreadyAddedStaticPageIds],
  );

  function handleAddInheritedPage(staticPage: import("./CanvasLeftSidebar").InheritedStaticPage) {
    if (readOnly) return;
    const page: ProposalDocumentLayoutPage = {
      id: getPageId(),
      title: staticPage.title,
      type: "custom",
      order: internalLayout.pages.length,
      settings: { source_static_page_id: staticPage.id },
      sections: [
        {
          id: getSectionId(),
          title: "Content",
          type: "content",
          order: 0,
          layoutMode: "stack",
          settings: {},
          elements: [
            {
              id: getElementId(),
              type: "rich_text",
              label: staticPage.title,
              content: { html: staticPage.contentHtml || "<p></p>" },
              style: {},
            },
          ],
        },
      ],
    };
    const next = reindexPages([...internalLayout.pages, page]);
    commitPages(next);
    setSelectedPageId(page.id ?? null);
  }

  // ---------------------------------------------------------------------------
  // Page IDs for DndContext awareness (left sidebar sortable)
  // ---------------------------------------------------------------------------

  const pageIds = internalLayout.pages.map((p) => `pg:${p.id ?? ""}`);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Wrap page list in its own SortableContext so page drags work */}
      <SortableContext items={pageIds} strategy={verticalListSortingStrategy}>
        <div
          className={cn(
            "flex min-h-[860px] overflow-hidden rounded-[22px] border border-white/8 bg-[#111315] text-white",
            className,
          )}
        >
          {/* Left: pages + palette */}
          <CanvasLeftSidebar
            pages={internalLayout.pages}
            selectedPageId={selectedPageId}
            onSelectPage={(id) => setSelectedPageId(id)}
            onAddPage={handleAddPage}
            onDuplicatePage={handleDuplicatePage}
            onDeletePage={handleDeletePage}
            readOnly={readOnly}
            inheritedPages={availableInheritedPages}
            onAddInheritedPage={handleAddInheritedPage}
          />

          {/* Centre + floating inspector — right padding shifts canvas away from inspector */}
          <div className={cn("relative flex-1 overflow-hidden transition-[padding-right] duration-200", inspectorOpen && "pr-[300px]")}>
            <CanvasPageStage
              page={selectedPage}
              selection={selection}
              onSelectSection={(sectionId) =>
                setSelection({ kind: "section", sectionId })
              }
              onSelectElement={(sectionId, elementId) =>
                setSelection({ kind: "element", sectionId, elementId })
              }
              onClearSelection={() => setSelection(null)}
              onDeleteSection={handleDeleteSection}
              onDeleteElement={handleDeleteElement}
              onUpdateElement={handleUpdateElement}
              onAddSection={handleAddSection}
              onAddElement={handleAddElement}
              readOnly={readOnly}
              proposalContext={proposalContext}
              onUpdateLineItem={onUpdateLineItem}
              className="h-full"
            />

            {/* Floating inspector — overlays the right edge of the canvas */}
            <CanvasRightInspector
              page={selectedPage}
              selection={selection}
              isOpen={inspectorOpen}
              onToggle={() => setInspectorOpen((v) => !v)}
              onClearSelection={() => setSelection(null)}
              onSelectSection={(sectionId) =>
                setSelection({ kind: "section", sectionId })
              }
              onSelectElement={(sectionId, elementId) =>
                setSelection({ kind: "element", sectionId, elementId })
              }
              onChangePageTitle={handleChangePageTitle}
              onChangePage={handleChangePage}
              onDuplicatePage={() => selectedPageId && handleDuplicatePage(selectedPageId)}
              onDeletePage={() => selectedPageId && handleDeletePage(selectedPageId)}
              onUpdateSection={handleUpdateSection}
              onDeleteSection={handleDeleteSection}
              onUpdateElement={handleUpdateElement}
              onDeleteElement={handleDeleteElement}
              readOnly={readOnly}
            />
          </div>
        </div>
      </SortableContext>

      {/* Drag ghost shown while dragging */}
      <DragOverlay>
        {activeId ? <DragGhost id={activeId} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
